import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  MenuItem,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PieChartComponent from '../../components/PieChartComponent';
import BarChartComponent from '../../components/BarChartComponent';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { motion, AnimatePresence } from 'framer-motion';
import { FILTER_CONFIG } from '../PersonasStep/filterConfig';

function RenderResponses({
  userData,
  loading,
  error,
  animatedProgress,
  onGenerateResponses,
  readOnly // New prop
}) {
  const [viewMode, setViewMode] = useState('byQuestion');
  const [expandedPanel, setExpandedPanel] = useState(0);
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [expandedPersonaPanels, setExpandedPersonaPanels] = useState({});
  const [showAdvancedAnalysis, setShowAdvancedAnalysis] = useState({});
  const [hasToggled, setHasToggled] = useState(false);
  const [groupByAttributes, setGroupByAttributes] = useState({});

  const handleAccordionChange = (panel) => (_, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : null);
    setHasToggled(false);
  };
  const toggleExpandQuestion = (index) => {
    setExpandedQuestions((prev) => ({ ...prev, [index]: !prev[index] }));
  };
  const toggleExpandPersona = (id) => {
    setExpandedPersonaPanels((prev) => ({ ...prev, [id]: !prev[id] }));
  };
  const handleGroupByChange = (questionID, value) => {
    setGroupByAttributes((prev) => ({ ...prev, [questionID]: value }));
  };
  const handleToggleAdvancedAnalysis = (questionID) => {
    setShowAdvancedAnalysis((prev) => ({ ...prev, [questionID]: !prev[questionID] }));
  };
  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) setViewMode(newMode);
  };

  const groupResponsesByAttribute = (responses, personas, attribute, question) => {
    const result = {};
    const allGroups = new Set();
    
    // Initialize result with original order from question.responseOptions if available
    if (question && question.responseOptions) {
      question.responseOptions.forEach(option => {
        result[option] = { option };
      });
    }
    
    responses.forEach(({ response, personaID }) => {
      const attrValue = personas[personaID]?.[attribute] || 'Unknown';
      const options = Array.isArray(response) ? response : [response];
      allGroups.add(attrValue);
      options.forEach((option) => {
        if (!result[option]) result[option] = { option };
        result[option][attrValue] = (result[option][attrValue] || 0) + 1;
      });
    });
    const groups = Array.from(allGroups);
    Object.values(result).forEach((obj) => {
      groups.forEach((group) => {
        if (!(group in obj)) obj[group] = 0;
      });
    });
    
    // Filter out options with no responses and return in original order
    return Object.values(result).filter(item => {
      // Check if this option has any responses across all groups
      const hasResponses = groups.some(group => (item[group] || 0) > 0);
      return hasResponses;
    });
  };

  const organizeByQuestion = () => {
    if (!userData.surveyResponses) return [];
    const personaIds = userData.selectedPersonas ? Object.keys(userData.selectedPersonas) : [];
    return userData.surveyQuestions.map((question, index) => {
      const questionResponses = userData.surveyResponses.filter(
        (r) => r.questionID === question.questionID && personaIds.includes(r.personaID.toString())
      );
      return {
        question: { ...question, displayNumber: index + 1 },
        responses: questionResponses,
      };
    });
  };

  const organizeByPersona = () => {
    if (!userData.surveyResponses) return [];
    return Object.entries(userData.selectedPersonas || {}).map(([personaId, persona]) => {
      const personaResponses = userData.surveyResponses.filter(
        (r) => r.personaID.toString() === personaId.toString() && r.researchID === userData.researchID
      );
      const orderedResponses = userData.surveyQuestions
        .map((question) => personaResponses.find((r) => r.questionID === question.questionID))
        .filter(Boolean);
      return { persona, personaId, responses: orderedResponses };
    });
  };

  const renderResponsesByQuestion = () => {
    const organizedData = organizeByQuestion();
    return (
      <Box>
        {organizedData.map((item, index) => {
          const { question } = item;
          const questionId = question.questionID;
          const hasResponses = item.responses && item.responses.length > 0;
          const isExpanded = expandedQuestions[index] || false;
          const hasMore = item.responses.length > 5;
          const displayedResponses = isExpanded ? item.responses : item.responses.slice(0, 5);
          if (!hasResponses) {
            return (
              <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Question {item.question.displayNumber || index + 1}: {item.question.questionText}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No responses yet for this question.
                </Typography>
              </Paper>
            );
          }
          // Initialize responseCounts with original order from question.responseOptions
          const allResponseCounts = (question.responseOptions || []).map(option => ({
            option,
            count: 0
          }));
          
          // Count responses while preserving original order
          item.responses.forEach((response) => {
            if (Array.isArray(response.response)) {
              response.response.forEach((ans) => {
                const found = allResponseCounts.find((r) => r.option === ans);
                if (found) {
                  found.count += 1;
                } else {
                  // If response not in original options, add it at the end
                  allResponseCounts.push({ option: ans, count: 1 });
                }
              });
            } else if (typeof response.response === 'string') {
              const found = allResponseCounts.find((r) => r.option === response.response);
              if (found) {
                found.count += 1;
              } else {
                // If response not in original options, add it at the end
                allResponseCounts.push({ option: response.response, count: 1 });
              }
            }
          });
          
          // Filter out options with zero counts for the pie chart
          const responseCounts = allResponseCounts.filter(item => item.count > 0);
          return (
            <Accordion
              key={index}
              expanded={expandedPanel === index}
              onChange={handleAccordionChange(index)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
                    Question {item.question.displayNumber || index + 1}: {item.question.questionText}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ mb: 1 }}></Box>
                {item.question.questionType === 'multiple-choice' && expandedPanel === index && (
                  <Paper sx={{ p: 3, mt: 4, mb: 4, boxShadow: 1, borderRadius: 2, overflow: 'hidden' }}>
                    <Box sx={{ position: 'relative', height: 450, overflow: 'hidden' }}>
                      <AnimatePresence mode="wait">
                        {!showAdvancedAnalysis[questionId] ? (
                          <motion.div
                            key="pie"
                            initial={{ x: 300, opacity: 0, scale: 0.8 }}
                            animate={{ x: 0, opacity: 1, scale: 1 }}
                            exit={{ x: -300, opacity: 0, scale: 0.8 }}
                            transition={{ duration: 0.4 }}
                          >
                            <PieChartComponent
                              questionText={item.question.questionText}
                              responseCounts={responseCounts}
                              disableAnimation={hasToggled}
                            />
                          </motion.div>
                        ) : (
                          <motion.div
                            key="bar"
                            initial={{ x: 300, opacity: 0, scale: 0.8 }}
                            animate={{ x: 0, opacity: 1, scale: 1 }}
                            exit={{ x: 300, opacity: 0, scale: 0.8 }}
                            transition={{ duration: 0.4 }}
                          >
                            <BarChartComponent
                              questionText={item.question.questionText}
                              groupedResponses={groupResponsesByAttribute(
                                item.responses,
                                userData.selectedPersonas,
                                groupByAttributes[questionId] || 'gender',
                                item.question
                              )}
                              attribute={groupByAttributes[questionId] || 'gender'}
                            />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={showAdvancedAnalysis[questionId] ? <ArrowBackIcon /> : null}
                        endIcon={showAdvancedAnalysis[questionId] ? null : <ArrowForwardIcon />}
                        onClick={() => handleToggleAdvancedAnalysis(questionId)}
                      >
                        {showAdvancedAnalysis[questionId]
                          ? 'Back to Basic Analysis'
                          : 'See Grouped Analysis'}
                      </Button>
                      {showAdvancedAnalysis[questionId] && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">Group responses by:</Typography>
                          <TextField
                            select
                            label="Attribute"
                            value={groupByAttributes[questionId] || 'gender'}
                            onChange={(e) => handleGroupByChange(questionId, e.target.value)}
                            size="small"
                            sx={{ minWidth: 200 }}
                          >
                            {Object.values(FILTER_CONFIG).map((filter) => (
                              <MenuItem key={filter.apiKey} value={filter.apiKey}>
                                {filter.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                )}
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Persona</TableCell>
                        <TableCell>Response</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayedResponses.map((response, rIndex) => {
                        const persona = userData.selectedPersonas?.[response.personaID];
                        return (
                          <TableRow key={rIndex}>
                            <TableCell>
                              {persona?.name || `Persona ${parseInt(response.personaID)}`}
                            </TableCell>
                            <TableCell>
                              {Array.isArray(response.response)
                                ? response.response.map((ans, i) => (
                                    <React.Fragment key={i}>
                                      {ans}
                                      {i < response.response.length - 1 && <br />}
                                    </React.Fragment>
                                  ))
                                : response.response}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {hasMore && (
                    <Button onClick={() => toggleExpandQuestion(index)} sx={{ mt: 2 }}>
                      {isExpanded ? 'Show Less' : 'Show More'}
                    </Button>
                  )}
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    );
  };

  const renderResponsesByPersona = () => {
    const organizedData = organizeByPersona();
    return (
      <Box>
        {organizedData.map((item, index) => {
          const personaId = item.personaId;
          const isExpanded = expandedPersonaPanels[personaId] || false;
          return (
            <Accordion
              key={index}
              expanded={isExpanded}
              onChange={() => toggleExpandPersona(personaId)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">
                  {item.persona.name || `Persona ${parseInt(item.personaId)}`} ({item.persona.age_range}, {item.persona.gender})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Question</TableCell>
                        <TableCell>Response</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {item.responses.map((response, rIndex) => {
                        const questionIndex = userData.surveyQuestions.findIndex(
                          (q) => q.questionID === response.questionID
                        );
                        const question = userData.surveyQuestions.find(
                          (q) => q.questionID === response.questionID
                        );
                        return (
                          <TableRow key={rIndex}>
                            <TableCell>
                              Question {questionIndex + 1}: {question?.questionText || response.questionID}
                            </TableCell>
                            <TableCell>
                              {Array.isArray(response.response)
                                ? response.response.map((ans, i) => (
                                    <React.Fragment key={i}>
                                      {ans}
                                      {i < response.response.length - 1 && <br />}
                                    </React.Fragment>
                                  ))
                                : typeof response.response === 'string'
                                ? response.response.split('\n').map((line, i) => (
                                    <React.Fragment key={i}>
                                      {line}
                                      {i < response.response.split('\n').length - 1 && <br />}
                                    </React.Fragment>
                                  ))
                                : JSON.stringify(response.response)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    );
  };

  // Main render logic
  const hasResponses = userData.surveyResponses && userData.surveyResponses.length > 0;
  if ((userData.responsesStatus === 'PENDING' && !hasResponses) || loading) {
    return (
      <Box mx="auto">
        <Paper sx={{ p: 4, mb: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress variant="determinate" value={100} size={150} thickness={4} sx={{ color: 'rgba(0, 0, 0, 0.1)' }} />
              <CircularProgress variant="determinate" value={animatedProgress} size={150} thickness={4} sx={{ position: 'absolute', left: 0 }} />
              <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="caption" component="div" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                  {`${Math.round(animatedProgress)}%`}
                </Typography>
              </Box>
            </Box>
            <Typography variant="h6" sx={{ mt: 2 }}>
              Generating responses...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This typically takes about 5 minutes to complete. You can stay on this page to see the responses as soon as they're ready, or come back later.
            </Typography>
          </Box>
        </Paper>
      </Box>
    );
  }
  if (userData.isGenerating) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress size={30} /> {/* Use indeterminate progress for passive waiting */}
        <Typography variant="body1" sx={{ mt: 2 }}>
          Responses are being generated in another tab or session
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          Results will appear here as they become available
        </Typography>
      </Box>
    );
  }
  if (hasResponses) {
    return (
      <Box mx="auto">
        <Box sx={{ mb: 2 }}>
          <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewModeChange} aria-label="view mode">
            <ToggleButton value="byQuestion" aria-label="by question">
              View by Question
            </ToggleButton>
            <ToggleButton value="byPersona" aria-label="by persona">
              View by Persona
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {viewMode === 'byQuestion' ? renderResponsesByQuestion() : renderResponsesByPersona()}
      </Box>
    );
  }
  
  // Default case (No responses)
  return (
      <Paper sx={{ p: 4, mb: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          No responses have been generated yet
        </Typography>
        
        {!readOnly ? (
          <>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Click the "Generate New Responses" button below to generate responses for all questions from your selected personas.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button variant="contained" onClick={onGenerateResponses} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Generate New Responses'}
          </Button>
        </Box>
        {error && (
          <Alert severity={userData.responsesStatus === 'PARTIAL' ? "warning" : "error"} sx={{ mt: 2 }}>
            {error}
            {userData.responsesStatus === 'PARTIAL' && (
              <Button 
                color="inherit" 
                size="small" 
                onClick={onGenerateResponses}
                sx={{ ml: 2 }}
              >
                Try Again
              </Button>
            )}
          </Alert>
        )}
          </>
        ) : (
          <Typography variant="body1" color="text.secondary">
            This research does not have any generated responses to display.
          </Typography>
        )}
      </Paper>
  );
}

export default RenderResponses;
